import { cookieService } from "@/lib/cookie/cookieService";
import { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";
import { AppError } from "@/lib/utils/errors";
import { isClient } from "@/lib/utils/client";

const BASE_ENDPOINT = process.env.NEXT_PUBLIC_API_ENDPOINT || "";

interface FetchOptions extends RequestInit {
  params?: Record<string, string>;
}

export class ApiClient {
  private readonly endpoint: string;
  private accessTokenAlreadySet: boolean = false;
  private lang: string = "";
  private accessToken: string | null = null;
  private cookies?: (() => Promise<ReadonlyRequestCookies>) | undefined;

  constructor(endpoint?: string) {
    this.endpoint = endpoint || BASE_ENDPOINT;
    if (isClient()) {
      this.getAccessToken();
    }
  }

  setCookies(cookies: (() => Promise<ReadonlyRequestCookies>) | undefined) {
    this.cookies = cookies;
  }

  async getLang() {
    if (isClient()) {
      this.lang = cookieService.get("i18next") || "vi";
    } else {
      this.lang =
        (await cookieService.getServer("i18next", this.cookies)) || "vi";
    }
  }

  setToken(token: string | null) {
    this.accessTokenAlreadySet = true;
    this.accessToken = token;
  }

  async getToken() {
    return await this.getAccessToken();
  }

  async getAccessToken() {
    if (isClient()) {
      if (
        (!this.accessToken || this.accessToken.length === 0) &&
        !this.accessTokenAlreadySet
      ) {
        const accessToken =
          cookieService.get("token", { encrypt: true }) || null;
        this.accessToken = accessToken;
        this.accessTokenAlreadySet = true;
      } else {
        return this.accessToken;
      }
    } else {
      return (
        (await cookieService.getServer("token", this.cookies, undefined, {
          encrypt: true,
        })) || null
      );
    }
  }

  async fetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    await this.getLang();
    const accessToken = await this.getAccessToken();
    const { params, ...fetchOptions } = options;

    // Add query params if they exist
    const url = new URL(`${endpoint.length > 0 ? endpoint : this.endpoint}`);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    // Default headers
    const headers = {
      "x-language": this.lang,
      ...fetchOptions.headers,
    };
    if (accessToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${accessToken}`;
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });
    if (!response.ok) {
      if (response.body) {
        const error = await response.json();
        const errorData = {
          code: error.code || response.status,
          message: error.message || response.statusText,
          reason: error.reason || error.message,
          details: error.details || [],
        };
        throw new AppError(errorData);
      }
      const errorData = {
        code: response.status,
        message: response.statusText,
        reason: response.statusText,
        details: [],
      };
      throw new AppError(errorData);
    }
    return response.json();
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const apiClient = new ApiClient();
